import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { error as errorNotif } from "react-notification-system-redux";
import { useNavigate, useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import PropTypes from "prop-types";

import breakpoints from "_constants/breakpoints";

import MonitoringReports from "components/OrderDetail/Results/MonitoringReports/MonitoringReports";
import TrademarkPrivateContactInfoSubscription from "components/Subscriptions/Details/TrademarkPrivateContactInfoSubscription";
import TrademarkPrivacyPackageSubscription from "components/Subscriptions/Details/TrademarkPrivacyPackageSubscription";

import { SpinnerBlock } from "widget/Spinner";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1360px;
  /* padding: 32px; */
  padding: 32px 0 24px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 48px;
  animation: 0.2s linear fadeIn;
  @keyframes fadeIn {
    from {
      opacity: 0%;
    }
    to {
      opacity: 100%;
    }
  }

  ${({ theme: { mobile } }) =>
    !mobile
      ? css`
          @media (max-width: 1350px) {
            width: 90%;
          }
        `
      : css`
          align-items: center;
        `}
`;

const Title = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  text-align: left;
  color: #03589c;
  margin-bottom: 24px;

  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      font-size: 22px;
      text-align: center;
      border-bottom: none;

      @media (max-width: ${breakpoints.mobileM}) {
        font-size: 18px;
        padding: 0;
      }
    `}
`;

const Subtitle = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: 20px;
  text-align: left;
  color: #03589c;
  margin-bottom: 16px;

  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      font-size: 18px;
      text-align: center;
      border-bottom: none;

      @media (max-width: ${breakpoints.mobileM}) {
        font-size: 16px;
      }
    `}
`;

const Buttons = styled.div`
  padding: 16px;
`;

const Button = styled.button`
  width: 209px;
  height: 42px;
  margin-bottom: 24px;
  background: #f37217;
  border: none;
  border-radius: 4px;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  color: #fafaff;
`;

const ErrorMessage = styled.div`
  padding: 32px 16px;
  margin-bottom: 16px;
  border-radius: 12px;
  border: 1px solid #dc3545;
  color: #dc3545;
`;

const SubscriptionDetails = ({ scrollToContent }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orderId, productId } = useParams();

  const { ordersList, ordersListLoading } = useSelector(({ orders }) => orders);

  const [orderDetails, setOrderDetails] = useState(null);
  const [productDetails, setProductDetails] = useState(null);

  const monitoringReports = productDetails?.processingResult?.monitoringReports;

  const renderDetails = (type, loading) => {
    if (loading) return <SpinnerBlock />;

    switch (type) {
      case "TRADEMARK_Trademark_Monitoring":
        return <MonitoringReports monitoringReports={monitoringReports} />;

      case "TRADEMARK_Private_Contact_Information":
        return <TrademarkPrivateContactInfoSubscription productDetails={productDetails} />;

      case "TRADEMARK_Privacy_Package":
        return <TrademarkPrivacyPackageSubscription productDetails={productDetails} />;

      default:
        return (
          <ErrorMessage>
            {productDetails?.code?.subscription
              ? "This subscription is not supported for displaying yet."
              : "This product does not have a subscription."}
          </ErrorMessage>
        );
    }
  };

  useEffect(() => {
    scrollToContent();

    if (!orderId || !productId) {
      dispatch(errorNotif({ title: "Error", message: "Something went wrong..." }));
      navigate("/subscriptions");
    }
  }, []);

  useEffect(() => {
    if (ordersList.length && !ordersListLoading) {
      const currentOrder = ordersList?.find(({ uid }) => uid === orderId);

      if (!currentOrder) {
        dispatch(errorNotif({ title: "Error", message: "An order with this ID was not found" }));
        navigate("/subscriptions");
      } else {
        const currentProduct = currentOrder?.products?.find(({ id }) => id === productId);
        if (!currentProduct) {
          dispatch(errorNotif({ title: "Error", message: "A product with this ID was not found" }));
          navigate("/subscriptions");
        } else {
          setOrderDetails(currentOrder);
          setProductDetails(currentProduct);
        }
      }
    }
  }, [ordersList, ordersListLoading]);

  if (!ordersList.length || !productDetails) return null;

  return (
    <Container>
      <Title>Subscription Details</Title>
      <Subtitle>
        {orderDetails && productDetails ? `${productDetails?.code?.customerName} — ${orderDetails?.crmOrderId}` : ""}
      </Subtitle>
      {renderDetails(productDetails?.code?.code, ordersListLoading)}
      <Buttons>
        <Button onClick={() => navigate("/subscriptions")}>Go Back</Button>
      </Buttons>
    </Container>
  );
};

export default SubscriptionDetails;

SubscriptionDetails.propTypes = {
  scrollToContent: PropTypes.func,
};
